import BaseModal from "../BaseModal/BaseModal";
import styles from "./DeleteConfirmModal.module.css";

/**
 * Reusable delete confirmation modal.
 * @param {boolean} open
 * @param {function} onClose
 * @param {string} title - e.g. "Delete country"
 * @param {string} message - e.g. "Are you sure you want to delete Spain? This action cannot be undone."
 * @param {string} itemName - optional; if provided, message can be omitted and a default is shown
 * @param {function} onConfirm - async () => {}; called when user clicks Delete
 * @param {boolean} loading - disables buttons and shows loading on Delete
 * @param {string} confirmLabel - default "Delete"
 * @param {string} loadingLabel - shown on the confirm button while loading; default "Deleting…"
 * @param {string} cancelLabel - default "Cancel"
 * @param {boolean} hideConfirm - when true, render an info-only dialog (no confirm/Delete button)
 */
const DeleteConfirmModal = ({
  open,
  onClose,
  title = "Delete",
  message,
  itemName,
  onConfirm,
  loading = false,
  confirmLabel = "Delete",
  loadingLabel = "Deleting…",
  cancelLabel = "Cancel",
  hideConfirm = false,
}) => {
  const displayMessage =
    message ??
    (itemName != null && itemName !== ""
      ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.`
      : "Are you sure? This action cannot be undone.");

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      // Error handled by caller (e.g. toast); don't re-throw to avoid raw AxiosError overlay
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={title}
      width="440px"
      footer={
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancel}
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          {!hideConfirm && (
            <button
              type="button"
              className={styles.confirm}
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? loadingLabel : confirmLabel}
            </button>
          )}
        </div>
      }
    >
      <p className={styles.message}>{displayMessage}</p>
    </BaseModal>
  );
};

export default DeleteConfirmModal;
